/**
 * Kleine SVG-Diagramme ohne Bibliothek: Liniendiagramm mit Ereignismarkern und Fadenkreuz,
 * divergierende Balken für Vorher-nachher-Vergleiche.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Kategoriale Farben für die Wählergruppen (geprüft auf dunklem Grund, auch bei Farbsehschwäche). */
export const GROUP_COLORS: Record<string, string> = { rent: '#3987e5', fam: '#d95926', arb: '#199e70', unt: '#c98500', jung: '#d55181' };

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null); const [w, setW] = useState(600);
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const set = () => setW(Math.max(260, Math.round(el.getBoundingClientRect().width)));
    set();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(set); ro.observe(el); return () => ro.disconnect();
  }, []);
  return [ref, w];
}

export type Series = { id: string; label: string; color: string; values: (number | null)[]; width?: number };
export type Marker = { i: number; good?: boolean; label: string; kind: string };

const ICON: Record<string, string> = { law: '§', crisis: '!', scandal: '?', ltw: 'L', budget: '€', fail: '×' };

/**
 * Liniendiagramm. `x` sind die Beschriftungen der Punkte (z. B. Monate), `ref` eine waagerechte
 * Bezugslinie (z. B. Wahlergebnis). Marker erscheinen als Symbole in einer Leiste unter der Kurve.
 */
export function LineChart({ series, x, min, max, unit = '', refLine, markers = [], height = 220, title, fmt = (v) => String(Math.round(v)) }:
  { series: Series[]; x: string[]; min?: number; max?: number; unit?: string; refLine?: { v: number; label: string }; markers?: Marker[]; height?: number; title: string; fmt?: (v: number) => string }) {
  const [wrap, W] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = x.length;
  const all = series.flatMap((s) => s.values.filter((v): v is number => v != null)).concat(refLine ? [refLine.v] : []);
  if (!n || !all.length) return <div className="chart empty sub">Noch keine Daten. Der Verlauf beginnt mit dem ersten Monat.</div>;
  const lo0 = min ?? Math.min(...all), hi0 = max ?? Math.max(...all), pad = (hi0 - lo0) * .12 || 2;
  const lo = min ?? Math.floor(lo0 - pad), hi = max ?? Math.ceil(hi0 + pad);
  const directLabels = series.length <= 4;
  const L = 34, R = directLabels ? 88 : 12, T = 10, MK = markers.length ? 22 : 0, B = 22 + MK, H = height;
  const pw = W - L - R, ph = H - T - B;
  const X = (i: number) => L + (n === 1 ? pw / 2 : (i / (n - 1)) * pw);
  const Y = (v: number) => T + (1 - (v - lo) / (hi - lo || 1)) * ph;
  const ticks = niceTicks(lo, hi, 4);
  const path = (vals: (number | null)[]) => { let d = '', pen = false; vals.forEach((v, i) => { if (v == null) { pen = false; return; } d += `${pen ? 'L' : 'M'}${X(i).toFixed(1)},${Y(v).toFixed(1)}`; pen = true; }); return d; };
  const xt = [0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i);
  const move = (e: React.PointerEvent<SVGRectElement>) => { const r = (e.target as SVGRectElement).getBoundingClientRect(); const px = e.clientX - r.left; setHover(Math.max(0, Math.min(n - 1, Math.round((px / r.width) * (n - 1))))); };
  // Direkte Beschriftungen am Linienende, Überlappungen auseinanderschieben
  const ends = directLabels ? series.map((s) => { let k = s.values.length - 1; while (k >= 0 && s.values[k] == null) k--; return { s, y: k >= 0 ? Y(s.values[k] as number) : -99 }; }).filter((e) => e.y > -99).sort((a, b) => a.y - b.y) : [];
  for (let i = 1; i < ends.length; i++) if (ends[i].y - ends[i - 1].y < 13) ends[i].y = ends[i - 1].y + 13;
  const hv = hover != null ? markers.filter((m) => m.i === hover) : [];
  return (
    <div className="chart" ref={wrap}>
      <svg width={W} height={H} role="img" aria-label={title}>
        {ticks.map((t) => <g key={t}><line x1={L} x2={L + pw} y1={Y(t)} y2={Y(t)} className="grid" /><text x={L - 6} y={Y(t) + 4} textAnchor="end" className="ax">{fmt(t)}{unit}</text></g>)}
        {refLine && <g><line x1={L} x2={L + pw} y1={Y(refLine.v)} y2={Y(refLine.v)} className="refl" /><text x={L + pw - 4} y={Y(refLine.v) + (Y(refLine.v) > T + ph - 14 ? -5 : 13)} textAnchor="end" className="ax">{refLine.label}</text></g>}
        {xt.map((i) => <text key={i} x={X(i)} y={T + ph + 16} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className="ax">{x[i]}</text>)}
        {series.map((s) => <path key={s.id} d={path(s.values)} fill="none" stroke={s.color} strokeWidth={s.width ?? 2} strokeLinejoin="round" strokeLinecap="round" />)}
        {ends.map((e) => <text key={e.s.id} x={L + pw + 8} y={e.y + 4} className="dl">{e.s.label}</text>)}
        {markers.map((m, k) => <g key={k} className={'mk ' + (m.good === true ? 'good' : m.good === false ? 'bad' : '')} transform={`translate(${X(m.i)},${T + ph + 30})`}><circle r="7" /><text y="3.5" textAnchor="middle">{ICON[m.kind] ?? '•'}</text></g>)}
        {hover != null && <line x1={X(hover)} x2={X(hover)} y1={T} y2={T + ph} className="xh" />}
        {hover != null && series.map((s) => s.values[hover] != null && <circle key={s.id} cx={X(hover)} cy={Y(s.values[hover] as number)} r="4" fill={s.color} className="hp" />)}
        <rect x={L} y={T} width={pw} height={ph + MK + 8} fill="transparent" onPointerMove={move} onPointerDown={move} onPointerLeave={() => setHover(null)} />
      </svg>
      {hover != null && <div className="tip" style={{ left: Math.min(W - 200, Math.max(0, X(hover) - 90)) }}>
        <b>{x[hover]}</b>
        {series.map((s) => s.values[hover] != null && <span key={s.id}><i style={{ background: s.color }} />{s.label}: <b className="num">{fmt(s.values[hover] as number)}{unit}</b></span>)}
        {hv.map((m, k) => <span key={'m' + k} className={m.good === true ? 'up' : m.good === false ? 'down' : ''}>{ICON[m.kind]} {m.label}</span>)}
      </div>}
      {!directLabels && <div className="legend">{series.map((s) => <span key={s.id}><i style={{ background: s.color }} />{s.label}</span>)}</div>}
      {markers.length > 0 && <div className="legend sub"><span>§ Gesetz in Kraft</span><span>× gescheitert</span><span>! Krise</span><span>? Affäre</span><span>L Landtagswahl</span></div>}
    </div>
  );
}

function niceTicks(lo: number, hi: number, k: number): number[] {
  const span = hi - lo || 1, step0 = span / k, mag = Math.pow(10, Math.floor(Math.log10(step0)));
  const step = [1, 2, 2.5, 5, 10].map((f) => f * mag).find((s) => s >= step0) ?? mag * 10;
  const out: number[] = []; for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(Math.round(v * 100) / 100);
  return out;
}

/** Divergierende Balken: Veränderung in Prozent, gut nach rechts, schlecht nach links. */
export function DivergingBars({ rows, title }: { rows: { label: string; pct: number; good: number; from: string; to: string }[]; title: string }) {
  const [wrap, W] = useWidth<HTMLDivElement>();
  const lw = Math.min(190, W * .42), bw = W - lw - 8, mid = lw + bw / 2, max = Math.max(5, ...rows.map((r) => Math.abs(r.pct)));
  const rh = 26, H = rows.length * rh + 8;
  return (
    <div className="chart" ref={wrap}>
      <svg width={W} height={H} role="img" aria-label={title}>
        <line x1={mid} x2={mid} y1={0} y2={H} className="axis0" />
        {rows.map((r, i) => { const w = (Math.abs(r.pct) / max) * (bw / 2 - 40), right = r.good >= 0, x0 = right ? mid + 1 : mid - 1 - w, y = 4 + i * rh;
          return <g key={r.label}>
            <text x={0} y={y + 15} className="dl">{r.label}</text>
            <rect x={x0} y={y + 4} width={Math.max(1.5, w)} height={rh - 10} rx="3" className={r.good > 0 ? 'bar good' : r.good < 0 ? 'bar bad' : 'bar'}><title>{`${r.label}: ${r.from} → ${r.to}`}</title></rect>
            <text x={right ? x0 + Math.max(1.5, w) + 6 : x0 - 6} y={y + 15} textAnchor={right ? 'start' : 'end'} className={'ax ' + (r.good > 0 ? 'up' : r.good < 0 ? 'down' : '')}>{r.pct > 0 ? '+' : r.pct < 0 ? '−' : '±'}{Math.abs(r.pct).toFixed(Math.abs(r.pct) < 10 ? 1 : 0).replace('.', ',')} %</text>
          </g>; })}
      </svg>
    </div>
  );
}

/** Zeigt Kinder erst, wenn sie im Bild sind (für Animationen beim Hineinscrollen). */
export function InView({ children }: { children: (seen: boolean) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null); const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); } }, { threshold: .3 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return <div ref={ref}>{children(seen)}</div>;
}
